import * as React from "react";
import { cn } from "@/lib/utils";

export interface FloatingInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
	label: string;
	error?: string;
}

const FloatingInput = React.forwardRef<HTMLInputElement, FloatingInputProps>(
	({ className, label, error, ...props }, ref) => {
		const [isFocused, setIsFocused] = React.useState(false);
		const [hasValue, setHasValue] = React.useState(false);

		React.useEffect(() => {
			setHasValue(!!props.value);
		}, [props.value]);

		const isFloating = isFocused || hasValue;

		const charCount = typeof props.value === "string" ? props.value.length : 0;
		const maxLength = props.maxLength;
		const isAtLimit = maxLength !== undefined && charCount >= maxLength;
		const isNearLimit = maxLength !== undefined && charCount >= maxLength * 0.8;

		return (
			<div className='relative'>
				<input
					className={cn(
						"peer h-12 w-full rounded-lg border border-gray-300 bg-white px-4 text-sm transition-all duration-200 placeholder:text-transparent focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500",
						error &&
							"border-red-500 focus:border-red-500 focus:ring-red-500/20",
						isAtLimit &&
							!error &&
							"border-amber-400 focus:border-amber-500 focus:ring-amber-500/20",
						className
					)}
					ref={ref}
					onFocus={() => setIsFocused(true)}
					onBlur={() => setIsFocused(false)}
					placeholder={label}
					{...props}
				/>
				<label
					className={cn(
						"pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 bg-white px-1 text-sm text-gray-500 transition-all duration-200",
						"peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:text-xs peer-focus:text-blue-600",
						isFloating && "top-0 -translate-y-1/2 text-xs",
						isFloating && isFocused && "text-blue-600",
						error &&
							"top-0 -translate-y-1/2 text-red-500 peer-focus:text-red-600"
					)}
				>
					{label}
				</label>
				<div className='mt-1 flex items-start justify-between gap-2'>
					{error ? (
						<p className='text-xs text-red-600'>{error}</p>
					) : isAtLimit ? (
						<p className='text-xs text-red-600'>
							Reached the {maxLength-1} character limit
						</p>
					) : (
						<span />
					)}
				</div>
			</div>
		);
	}
);

FloatingInput.displayName = "FloatingInput";

export { FloatingInput };
